import { FunctionalComponent, Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { PaymentInstrumentType } from '../enums/Tokens';
import { VaultListItem, CardDetails, PayPalDetails } from '../types';
import { toggleClass } from '../utils/toggleClass';
import { ClassName } from '../enums/Checkout';
import { delay } from '../utils/delay';
import { usePresence } from '../utils/AnimatePresence/use-presence';

type VaultedProps = {
  vaultedItem: VaultListItem;
};

const CardContent: FunctionalComponent<VaultedProps> = ({ vaultedItem }) => {
  const details = vaultedItem.details as CardDetails;

  return (
    <Fragment>
      <img
        class='PrimerCheckout__savedPaymentMethodIcon'
        src={details.icon}
        alt={details.network}
      />
      <div class='PrimerCheckout__savedPaymentMethodContent'>
        <span class='PrimerCheckout__label PrimerCheckout__savedPaymentMethodDetail'>
          {details.name}
        </span>
        <span class='PrimerCheckout__label PrimerCheckout__savedPaymentMethodLabel'>
          {details.cardNumber}
        </span>
      </div>
    </Fragment>
  );
};

const Card = {
  Content: CardContent,

  getAriaLabel: ({ vaultedItem }) =>
    `${vaultedItem.details.network} ${vaultedItem.details.name}, ending with ${vaultedItem.details.last4Digits}`,
};

const PayPalContent: FunctionalComponent<VaultedProps> = ({ vaultedItem }) => {
  const details = vaultedItem.details as PayPalDetails;

  return (
    <Fragment>
      <img
        class='PrimerCheckout__savedPaymentMethodIcon'
        src={details.icon}
        alt=''
      />
      <div class='PrimerCheckout__savedPaymentMethodContent'>
        <span class='PrimerCheckout__label PrimerCheckout__savedPaymentMethodDetail'>
          {details.email}
        </span>
      </div>
    </Fragment>
  );
};

const PayPal = {
  Content: PayPalContent,

  getAriaLabel: ({ vaultedItem }) => `PayPal ${vaultedItem.details.email}`,
};

const Details = {
  [PaymentInstrumentType.CARD]: Card,
  [PaymentInstrumentType.PAYPAL_VAULTED]: PayPal,
};

type Props = {
  vaultedItem: VaultListItem;
  disabled?: boolean;
  selected?: boolean;
  onSelect?: () => void;
};

const PaymentMethodTokenItem: FunctionalComponent<Props> = ({
  vaultedItem,
  disabled = false,
  selected = false,
  onSelect,
}) => {
  const [isPresent, safeToRemove] = usePresence();
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (isPresent) {
      return;
    }

    (async () => {
      const paymentMethodContainer = containerRef.current;
      paymentMethodContainer.style.height = `${paymentMethodContainer.scrollHeight}px`;
      toggleClass(
        paymentMethodContainer,
        `${ClassName.SAVED_PAYMENT_METHOD_CONTAINER}--exiting`,
        true,
      );

      // Refresh layout
      paymentMethodContainer.getBoundingClientRect();
      paymentMethodContainer.style.height = '0px';

      await delay(300);
      safeToRemove?.();
    })();
  }, [isPresent]);

  const { Content, getAriaLabel } = Details[vaultedItem.type];
  const args = { vaultedItem };

  return (
    <div ref={containerRef} class='PrimerCheckout__savedPaymentMethodContainer'>
      <button
        type='button'
        data-token={vaultedItem.id}
        class={`PrimerCheckout__savedPaymentMethod PrimerCheckout__token ${
          selected ? 'PrimerCheckout--selected' : ''
        }`}
        aria-label={getAriaLabel?.(args)}
        aria-checked={selected}
        role='checkbox'
        onClick={onSelect}
        disabled={disabled}
      >
        {Content && <Content {...args} />}
      </button>
    </div>
  );
};

export default PaymentMethodTokenItem;
